import { useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../../App";
import { handleGraphQLErrors } from "../../../../utils/handleGraphQLError";
import { passwordValidator } from "../../../../utils/passwordValidator";
import { Icon } from "../../../Icon/Icon";
import { Button, ButtonLoading } from "../../Button/Button";
import { EditableInformationTextInput } from "../EditableInformation";
import "./EditableInformationPassword.css";

/**
 * EditableInformationPassword
 *
 * @param {object} props
 * @prop {string} label
 * @prop {(?string|?Date)} passwordChangedWhen
 * @prop {boolean} editable
 * @prop {boolean} defaultEdititingState
 * @prop {string} type
 * @prop {string} userEmail
 * @return {*}
 */
export const EditableInformationPassword = ({
  label,
  passwordChangedWhen = null,
  editable = true,
  defaultEditingState = false,
  type = "self",
  userEmail = "",
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [requestNewPassword] = useMutation(requestNewPasswordMutation);
  const [updateSSOUserPassword] = useMutation(updateSSOUserPasswordMutation, {
    update: (cache, data) => {
      const currentUser = cache.readQuery({ query: getUserInformationQuery });

      const updatedPasswordDate =
        data?.data?.updateSSOUserPassword?.passwordChangedAt;

      const newData = {
        ...currentUser,
        user: {
          ...currentUser.user,
          passwordChangedAt: updatedPasswordDate,
        },
      };

      cache.writeQuery({ query: getUserInformationQuery, data: newData });
    },
  });

  const handleUserPasswordUpdate = (inputData) => {
    if (type === "self") {
      const { oldPassword, newPassword, repeatNewPassword } = inputData;
      if (oldPassword !== "" || newPassword !== "") {
        // Mutate and update password for self
        if (newPassword === repeatNewPassword) {
          setIsLoading(true);
          updateSSOUserPassword({ variables: { oldPassword, newPassword } })
            .then(() => {
              setIsLoading(false);
              toast(t("toasts.passwordUpdated"), toastSuccessOptions);
              setIsEditing(false);
            })
            .catch((err) => {
              setIsLoading(false);
              handleGraphQLErrors(err);
            });
        }
      }
    } else {
      // Use the userEmail to trigger a reset request
      requestNewPassword({ variables: { email: userEmail } })
        .then(() => {
          toast(t("toasts.passwordResetSent"), toastSuccessOptions);
        })
        .catch((err) => {
          handleGraphQLErrors(err);
        });
    }
  };

  return (
    <div
      className={
        isEditing
          ? "EditableInformationDisplay EditableInformationPassword EditableInformationDisplay--editing EditableInformationPassword--editing"
          : "EditableInformationDisplay EditableInformationPassword"
      }
    >
      <div className="EditableInformationDisplay__label">
        <div className="EditableInformationDisplay__label--text">
          {t(label)}
        </div>
      </div>
      <div className="EditableInformationDisplay__value EditableInformationPassword__value">
        {!isEditing && (
          <div className="EditableInformationDisplay__value--text">
            **********
          </div>
        )}
        {isEditing && (
          <Formik
            initialValues={{
              oldPassword: "",
              newPassword: "",
              repeatNewPassword: "",
            }}
            onSubmit={handleUserPasswordUpdate}
            validationSchema={validationSchema}
          >
            {({ isValid, dirty }) => {
              return (
                <Form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <div className="EditableInformationDisplay__value--editing EditableInformationPassword__value">
                    <EditableInformationTextInput
                      name="oldPassword"
                      type="password"
                      label={t("onboardingForm.currentPassword")}
                      defaultEditingState={true}
                    />
                  </div>
                  <span className="EditableInformationPassword__seperator" />
                  <div className="EditableInformationDisplay__value--editing EditableInformationPassword__value">
                    <EditableInformationTextInput
                      name="newPassword"
                      type="password"
                      label={t("onboardingForm.newPassword")}
                      defaultEditingState={true}
                    />
                  </div>
                  <span className="EditableInformationPassword__seperator" />
                  <div className="EditableInformationDisplay__value--editing EditableInformationPassword__value">
                    <EditableInformationTextInput
                      name="repeatNewPassword"
                      type="password"
                      label={t("onboardingForm.repeatNewPassword")}
                      defaultEditingState={true}
                    />
                  </div>
                  <div className="EditableInformationDisplay__action EditableInformationPassword__action">
                    <Button
                      buttonStyle="EditableInformationDisplay__action-button"
                      click={() => setIsEditing(false)}
                    >
                      <Icon name="iconCross" width={14} height={14} />
                    </Button>
                    <Button
                      buttonStyle="EditableInformationDisplay__action-button"
                      type="submit"
                      disabled={!isValid || !dirty || isLoading}
                    >
                      {!isLoading &&
                        (console.log({ isValid, dirty }),
                        (<Icon name="iconCheckmark" width={14} height={18} />))}
                      {isLoading && <ButtonLoading />}
                      <div className="EditableInformationDisplay__action-button-text">
                        {t("generic.save")}
                      </div>
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
        {passwordChangedWhen && !isEditing && (
          <div className="EditableInformationDisplay__value--subtext">
            {`${t("profile.lastChanged")} ${passwordChangedWhen}`}
          </div>
        )}
      </div>
      {editable && !defaultEditingState && type === "self" && (
        <div
          className="EditableInformationDisplay__action"
          onClick={() => setIsEditing(true)}
        >
          {!isEditing && <Icon name="iconEdit" width={14} height={18} />}
        </div>
      )}
      {type !== "self" && (
        <div
          className="EditableInformationDisplay__action"
          onClick={() => handleUserPasswordUpdate()}
        >
          <div className="EditableInformationDisplay__action-button">
            <div className="EditableInformationDisplay__action-button-text">
              {t("users.sendResetLink")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const validationSchema = Yup.object().shape({
  oldPassword: Yup.string().required("validation.passwordRequired"),
  newPassword: passwordValidator.required("validation.passwordRequired"),
  repeatNewPassword: Yup.string().oneOf(
    [Yup.ref("newPassword"), null],
    "validation.passwordMatchesOtherField"
  ),
});

const requestNewPasswordMutation = gql`
  mutation requestSSOUserPasswordReset($email: String) {
    requestSSOUserPasswordReset(email: $email)
  }
`;

const updateSSOUserPasswordMutation = gql`
  mutation updateSSOUserPassword($oldPassword: String, $newPassword: String) {
    updateSSOUserPassword(oldPassword: $oldPassword, newPassword: $newPassword)
  }
`;

const getUserInformationQuery = gql`
  query {
    user {
      id
      email
      name
      passwordChangedAt
      createdAt
      prefferedLanguage
    }
  }
`;
